import type { TFunction } from "i18next";

import type { FilterKey } from "@/features/integrations/modules/applies/pages/overview/appliesFilterHelpers";
import {
  APPLIES_FILTER_PRESERVE_PARAM_KEYS,
  DATE_RANGE_PICKER_CONFIG,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
  getAppliesFilterKeys,
} from "@/features/integrations/modules/applies/pages/overview/AppliesPageConfig";
import type { CandidateFiltersData } from "@/features/integrations/modules/candidates/api";
import type { ModuleUiColumnDefinition, UiComponent } from "@/features/integrations/types";

export {
  APPLIES_FILTER_PRESERVE_PARAM_KEYS as CANDIDATES_FILTER_PRESERVE_PARAM_KEYS,
  DATE_RANGE_PICKER_CONFIG,
  DEFAULT_END_DATE,
  DEFAULT_START_DATE,
};

type CandidateFilterKey = keyof CandidateFiltersData;

const toCandidateFilterKeys = (keys: readonly FilterKey[]) =>
  keys.filter((key): key is CandidateFilterKey => key !== "applicationSourceType");

const ALL_CANDIDATE_FILTER_KEYS = toCandidateFilterKeys(getAppliesFilterKeys(false));
const CLIENT_CANDIDATE_FILTER_KEYS = toCandidateFilterKeys(getAppliesFilterKeys(true));

export function getCandidatesFilterKeys(clientScoped: boolean) {
  return clientScoped ? CLIENT_CANDIDATE_FILTER_KEYS : ALL_CANDIDATE_FILTER_KEYS;
}

// ---------------------------------------------------------------------------
// Metrics (report cards)
// ---------------------------------------------------------------------------

export const METRICS_CONFIG = {
  totalCandidates: {
    titleKey: "integrations.cockpit.candidates.totalCandidates" as const,
  },
  averageApplies: {
    titleKey: "integrations.cockpit.candidates.averageApplies" as const,
  },
  qualified: {
    titleKey: "integrations.cockpit.candidates.qualified" as const,
  },
  hireable: {
    titleKey: "integrations.cockpit.candidates.hireable" as const,
  },
} as const;

// ---------------------------------------------------------------------------
// Page / search
// ---------------------------------------------------------------------------

export const PAGE_CONFIG = {
  titleKey: "common.applies" as const,
  subtitleKey: "integrations.cockpit.candidates.subtitle" as const,
} as const;

// ---------------------------------------------------------------------------
// Table toolbar
// ---------------------------------------------------------------------------

export const TABLE_TOOLBAR_CONFIG = {
  // Names what the search actually matches. The backend search (shared with Applies) is a LIKE across
  // campaign name, client name, publication id, integration display name, and the RecruitNow match /
  // ATS candidate ids — so "Search applies" was wrong twice over above a candidate table.
  searchPlaceholderKey: "integrations.cockpit.candidates.searchCandidates" as const,
  dateRangeFilterKey: "dateRange" as const,
  rowIdColumn: "id" as const,
} as const;

// ---------------------------------------------------------------------------
// Table columns
// ---------------------------------------------------------------------------

// Qualified/Hireable are the candidate's current status (booleans) — rendered as a green check / dash
// by the "Boolean" column type. The candidate GUID is not shown (it stays the row id); the ATS id has
// no backend sort key → not sortable.
const ALL_COLUMNS: ModuleUiColumnDefinition[] = [
  { key: "clientName", label: "common.client", type: "String", showNullAs: "N/A" },
  { key: "atsCandidateId", label: "common.atsCandidateId", type: "String", showNullAs: "N/A", sortable: false },
  { key: "totalApplies", label: "common.totalApplies", type: "Int" },
  { key: "qualified", label: "common.qualified", type: "Boolean", textAlign: "center" },
  { key: "hireable", label: "common.hireable", type: "Boolean", textAlign: "center" },
];

// Client-scoped view drops the client column (a single client is implied).
const CLIENT_COLUMNS: ModuleUiColumnDefinition[] = ALL_COLUMNS.filter((c) => c.key !== "clientName");

export function getCandidatesColumns(clientScoped: boolean) {
  return clientScoped ? CLIENT_COLUMNS : ALL_COLUMNS;
}

// ---------------------------------------------------------------------------
// UI component tree (table toolbar + data table)
// ---------------------------------------------------------------------------

/** Builds the Candidates page UI component array. Requires `t` for the translated search placeholder. */
export function getCandidatesUiComponents(t: TFunction, clientScoped = false): UiComponent[] {
  return [
    {
      component: "tableToolbar",
      props: {
        search: {
          placeholder: t(TABLE_TOOLBAR_CONFIG.searchPlaceholderKey, "Search publication ID"),
          enabled: true,
        },
        filters: [
          {
            key: TABLE_TOOLBAR_CONFIG.dateRangeFilterKey,
            type: "DateRange",
            inlineWithSearch: true,
          },
        ],
      },
    },
    {
      component: "dataTable",
      props: {
        rowIdColumn: TABLE_TOOLBAR_CONFIG.rowIdColumn,
        columns: getCandidatesColumns(clientScoped),
      },
    },
  ];
}
